// Wire types shared by the customer + admin surfaces. Mirrors the StoreFront
// app definition (ClientCanvas/App-Definitions/storefront/1.0.0) and the iOS
// models in StoreFront/StoreFront/Models.

export interface MenuItem {
  item: string;
  price: string | null;
  description?: string | null;
  image?: string | null;
  special?: boolean;
  outOfStock?: boolean;
}
export interface MenuCategory { category: string; items: MenuItem[]; }

export interface Branding {
  colors: { primary: string; secondary: string };
  logoUrl: string | null;
}

export type Hours = Record<string, string>;

export type DeliveryProvider = 'self' | 'uber_direct' | 'doordash_drive';

/** Channels a store can pause. 'all' masks both (D-P2). */
export type PauseChannel = 'delivery' | 'pickup' | 'all';

/**
 * Durations are sent by NAME. The server resolves them against the STORE's
 * time zone — never this browser's, which would be whichever zone the owner
 * happens to be standing in.
 */
export type PauseDuration = '1h' | '2h' | 'rest_of_today' | 'tomorrow_morning' | 'indefinite';

/** The two that need `MyProfile.timezone` to be set before they can be used. */
export const WALL_CLOCK_DURATIONS: PauseDuration[] = ['rest_of_today', 'tomorrow_morning'];

export const PAUSE_DURATION_LABEL: Record<PauseDuration, string> = {
  '1h': '1 hour',
  '2h': '2 hours',
  rest_of_today: 'Rest of today',
  tomorrow_morning: 'Until tomorrow morning',
  indefinite: 'Until I turn it back on',
};

/** An active pause as the owner sees it — storefront_api.get_my_profile. */
export interface StorePause {
  channel: PauseChannel;
  note: string;
  pausedAt: string;
  resumesAt: string | null;
}

/** A time zone derived from the store address, for the owner to confirm. */
export interface TimezoneSuggestion {
  timezone: string;
  state: string;
  /** This state spans two zones — say so louder. */
  confirmRequired: boolean;
}

/** Why the server will not take a delivery order right now (D-P1). */
export type DeliveryRefusal =
  | 'DELIVERY_DISABLED'
  | 'DELIVERY_PAUSED'
  | 'DELIVERY_MIN_ORDER'
  | 'DELIVERY_OUT_OF_AREA'
  | 'DELIVERY_ADDRESS_UNRESOLVED';

/** One fulfillment channel as the customer site sees it — storefront_public. */
export interface ChannelAvailability {
  offered: boolean;
  /** Temporarily off with a note from the owner (D-P2). */
  paused: boolean;
  note: string | null;
  resumesAt: string | null;
}

export interface DeliveryAvailability extends ChannelAvailability {
  feeCents: number;
  minSubtotalCents: number;
  etaMinutes: number | null;
  radiusMiles: number | null;
  zips: string[];
}

export interface StoreFulfillment {
  pickup: ChannelAvailability;
  delivery: DeliveryAvailability;
}

/** Owner-side delivery settings — storefront_api.update_delivery_settings. */
export interface DeliverySettings {
  enabled: boolean;
  provider: DeliveryProvider;
  feeCents: number;
  minSubtotalCents: number;
  radiusMiles: number | null;
  zips: string[];
  etaMinutes: number | null;
}

/** Directory card — storefront_public.list_stores. */
export interface StoreCard {
  slug: string;
  displayName: string;
  logoUrl: string | null;
  primaryColor: string;
  about: string;
  categoriesCount: number;
  itemsCount: number;
}

/** Public store profile — storefront_public.get_store. */
export interface PublicStore {
  slug: string;
  displayName: string;
  branding: Branding;
  menu: MenuCategory[];
  hours: Hours;
  about: string;
  taxRateBps: number;
  address: string | null;
  fulfillment: StoreFulfillment;
  payments: { configured: boolean };
}

export interface PaymentsAvailability {
  configured: boolean;
  publishableKey: string | null;
  livemode: boolean | null;
  resolved?: string | null;
  reason?: string;
}

/** Signed-in user's bound profile — storefront_api.get_my_profile. */
export interface MyProfile {
  id: string;
  businessSlug: string;
  displayName: string;
  branding: Branding;
  menu: MenuCategory[];
  hours: Hours;
  about: string;
  taxRateBps: number;
  address: string | null;
  addressLat: number | null;
  addressLng: number | null;
  deliveryEnabled: boolean;
  deliveryProvider: DeliveryProvider;
  deliveryFeeCents: number;
  deliveryMinSubtotalCents: number;
  deliveryRadiusMiles: number | null;
  deliveryZips: string[];
  deliveryEtaMinutes: number | null;
  timezone: string | null;
  /** Only present while `timezone` is unset. */
  timezoneSuggestion: TimezoneSuggestion | null;
  pauses: Partial<Record<PauseChannel, StorePause>>;
  logoMediaId: string | null;
  createdAt: string;
  role: 'owner' | 'customer' | string;
  payments: PaymentsAvailability;
}

export type OrderStatus =
  | 'pending' | 'confirmed' | 'preparing' | 'ready'
  | 'out_for_delivery' | 'picked_up' | 'delivered' | 'cancelled';
export type PaymentMethod = 'pay_at_store' | 'online';
export type PaymentStatus = 'unpaid' | 'paid' | 'refunded' | 'partially_refunded' | string;
export type Fulfillment = 'pickup' | 'delivery';

export interface OrderLine {
  item: string;
  category?: string | null;
  price?: string | null;
  unitCents?: number;
  quantity: number;
  lineCents?: number;
  notes?: string | null;
}

export interface Order {
  id: string;
  placedByUser: string;
  items: OrderLine[];
  totalCents: number;
  subtotalCents: number;
  taxCents: number;
  deliveryFeeCents: number;
  deliveryProvider: DeliveryProvider | null;
  courierRef: string | null;
  courierStatus: string | null;
  courierTrackingUrl: string | null;
  taxRateBps: number;
  currency: string;
  paymentMethod: PaymentMethod;
  paymentStatus: PaymentStatus;
  paymentDetail: Record<string, unknown> | null;
  paidAt: string | null;
  refundedCents: number;
  channel: 'cart' | 'chat';
  status: OrderStatus;
  fulfillment: Fulfillment;
  deliveryAddress: string | null;
  /** Checkout contact (G-P2) — present on orders placed since guest checkout. */
  customerName?: string | null;
  customerEmail?: string | null;
  customerPhone?: string | null;
  etaAt: string | null;
  cancelReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CartLine { item: string; quantity: number; notes?: string; }

/** The delivery half of a quote: the fee, or why there isn't one. */
export interface QuoteDelivery {
  offered: boolean;
  available: boolean;
  code: DeliveryRefusal | null;
  message: string | null;
  feeCents: number;
  minSubtotalCents: number;
  etaMinutes: number | null;
  resumesAt: string | null;
}

/** Whole-store availability, separate from the delivery-specific answer: a
 *  paused store has to stop a pickup checkout too. */
export interface QuoteOrdering {
  open: boolean;
  code: 'ORDERING_PAUSED' | 'PICKUP_PAUSED' | null;
  message: string | null;
  resumesAt: string | null;
}

export interface Quote {
  lines: OrderLine[];
  subtotalCents: number;
  taxCents: number;
  deliveryFeeCents: number;
  totalCents: number;
  taxRateBps: number;
  fulfillment: Fulfillment;
  ordering: QuoteOrdering;
  delivery: QuoteDelivery;
  payments: PaymentsAvailability;
}

export interface PaymentIntentStart {
  orderId: string;
  clientSecret: string;
  publishableKey: string;
  amountCents: number;
  currency: string;
  livemode: boolean;
  subtotalCents: number;
  taxCents: number;
  deliveryFeeCents: number;
  totalCents: number;
  taxRateBps: number;
}

export interface MediaItem {
  id: string;
  profileId: string;
  url: string;
  title: string | null;
  filename: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  source: 'scraped' | 'menu' | 'upload' | 'logo' | string;
  uploadedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export const ALLOWED_TRANSITIONS: Record<OrderStatus, OrderStatus[]> = {
  pending: ['confirmed', 'cancelled'],
  confirmed: ['preparing', 'ready', 'cancelled'],
  preparing: ['ready', 'cancelled'],
  ready: ['out_for_delivery', 'picked_up', 'delivered', 'cancelled'],
  out_for_delivery: ['delivered', 'cancelled'],
  picked_up: [],
  delivered: [],
  cancelled: [],
};

export const STATUS_LABEL: Record<OrderStatus, string> = {
  pending: 'New',
  confirmed: 'Confirmed',
  preparing: 'Preparing',
  ready: 'Ready',
  out_for_delivery: 'Out for delivery',
  picked_up: 'Picked up',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

export function money(cents: number | null | undefined, currency = 'usd'): string {
  const n = typeof cents === 'number' ? cents : 0;
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(n / 100);
  } catch {
    return `$${(n / 100).toFixed(2)}`;
  }
}

/**
 * Every IANA zone the browser knows, for the time-zone picker.
 * `Intl.supportedValuesOf` is ES2022 and this project's lib target predates it,
 * so it is reached through a cast and degrades to an empty list (the field is a
 * free-text input with a datalist, so an empty list just means no suggestions).
 */
export function allTimeZones(): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
  try { return typeof intl.supportedValuesOf === 'function' ? intl.supportedValuesOf('timeZone') : []; }
  catch { return []; }
}

/** "back at 1:00 PM" / "back Friday at 8:00 AM" — in the reader's own zone,
 *  which is right for a customer looking at a clock next to them. */
export function resumesLabel(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return sameDay ? `back at ${time}` : `back ${d.toLocaleDateString([], { weekday: 'long' })} at ${time}`;
}

export function pct(bps: number): string {
  return `${(bps / 100).toFixed(bps % 100 === 0 ? 0 : 2)}%`;
}

export interface WebDomainStatus {
  domain: string;
  id: string | null;
  applePay: 'active' | 'inactive' | 'error';
  error: string | null;
}

export interface PaymentAccountStatus {
  configured: boolean;
  resolved?: 'scope' | 'app_default' | 'none' | string | null;
  scopeKey?: string | null;
  providerAccountId?: string | null;
  publishableKey?: string | null;
  livemode?: boolean | null;
  displayName?: string | null;
  status?: string | null;
  reason?: string;
}
